import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { getGoogleMapsApiKey } from '../services/api';
import { useAuth } from './AuthContext';

const libraries: ("places" | "geometry" | "visualization")[] = ["places", "geometry", "visualization"];

interface GoogleMapsContextType {
    isLoaded: boolean;
    loadError: Error | undefined;
    apiKey: string | null;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
    isLoaded: false,
    loadError: undefined,
    apiKey: null,
});

export const useGoogleMaps = () => useContext(GoogleMapsContext);

/**
 * Single Google Maps loader for the entire admin panel.
 * Prevents the "Loader must not be called again with different options" crash.
 */
export const GoogleMapsProvider = ({ children }: { children: ReactNode }) => {
    const { token } = useAuth();
    const [apiKey, setApiKey] = useState<string | null>(null);
    const [keyLoading, setKeyLoading] = useState(true);

    // The key endpoint is admin-only now, so it is fetched once someone is signed in (and again after a login on
    // the same page). Once loaded it is kept: the Maps loader can only be initialised with one key.
    useEffect(() => {
        if (apiKey) return;
        if (!token) { setKeyLoading(false); return; }
        setKeyLoading(true);
        getGoogleMapsApiKey()
            .then((key) => {
                setApiKey(key);
                setKeyLoading(false);
            })
            .catch(() => {
                setApiKey(null);
                setKeyLoading(false);
            });
    }, [token, apiKey]);

    // Don't initialize the loader until we have the key
    if (keyLoading || !apiKey) {
        return (
            <GoogleMapsContext.Provider value={{ isLoaded: false, loadError: undefined, apiKey: null }}>
                {children}
            </GoogleMapsContext.Provider>
        );
    }

    return <GoogleMapsInner apiKey={apiKey}>{children}</GoogleMapsInner>;
};

const GoogleMapsInner = ({ apiKey, children }: { apiKey: string; children: ReactNode }) => {
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: apiKey,
        libraries,
    });

    return (
        <GoogleMapsContext.Provider value={{ isLoaded, loadError, apiKey }}>
            {children}
        </GoogleMapsContext.Provider>
    );
};

export default GoogleMapsProvider;
